import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useUnit } from "effector-react";
import { useTranslation } from "react-i18next";
import { devicesEntity } from "@entities/devices";
import { defaultPrompts } from "@shared/configs/experiments";

type Message = { isAi: boolean; message: string };

type DeviceAssistantPanelProps = {
  onSubmit: (value: string) => void;
  onReset?: () => void;
  messages: Message[];
  thinking?: boolean;
  className?: string;
};

export const DeviceAssistantPanel: FC<DeviceAssistantPanelProps> = ({
  onSubmit,
  onReset,
  messages,
  thinking = false,
  className,
}) => {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const inlineInputRef = useRef<HTMLInputElement | null>(null);
  const expandedInputRef = useRef<HTMLInputElement | null>(null);
  const inlineChatEndRef = useRef<HTMLDivElement | null>(null);
  const expandedChatEndRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [experiments, selectedExperimentId, setExperimentId] = useUnit([
    devicesEntity.$experiments,
    devicesEntity.$experimentId,
    devicesEntity.setExperimentId,
  ]);

  const selectedExperiment = useMemo(() => {
    if (selectedExperimentId == null) return null;
    return experiments.find((e) => e.id === selectedExperimentId) ?? null;
  }, [experiments, selectedExperimentId]);

  const allPrompts = selectedExperiment?.predefinedPrompts ?? [];

  const emptyStatePrompts = allPrompts.length > 0 ? allPrompts : defaultPrompts

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() !== "") {
      onSubmit(input);
      setInput("");
    }
  };

  const handleReset = () => {
    if (onReset) onReset();
  };

  const handlePromptClick = (prompt: string) => {
    if (thinking) return;
    onSubmit(prompt);
    setInput("");
    (isExpanded ? expandedInputRef : inlineInputRef).current?.focus();
  };

  useEffect(() => {
    const endRef = (isExpanded ? expandedChatEndRef : inlineChatEndRef).current;
    endRef?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [isExpanded, messages.length, thinking]);

  const renderEmptyState = (variant: "inline" | "expanded") => {
    void variant;

    return (
      <div className="mt-6">
        <p className="text-gray-500 text-sm italic text-center">
          {t("assistant.empty_hint_prefix")}
        </p>

        <div
          className="mt-4 flex flex-wrap justify-center gap-2"
        >
          {emptyStatePrompts.map((prompt, idx) => (
            <button
              key={`${prompt}-${idx}`}
              type="button"
              onClick={() => handlePromptClick(prompt)}
              className="group inline-flex items-center gap-2 rounded-full border border-cyan-500/15 bg-gradient-to-r from-cyan-500/10 to-blue-500/5 px-3 py-2 text-sm text-cyan-100 hover:from-cyan-500/18 hover:to-blue-500/10 hover:border-cyan-400/25 transition-colors"
              disabled={thinking}
              title={prompt}
            >
              <span className="max-w-[520px] truncate text-left">{prompt}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {isExpanded && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="relative w-full max-w-3xl lg:max-w-4xl xl:max-w-5xl h-[85vh]">
            <motion.div
              className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-2xl border border-gray-700 shadow-[0_0_40px_-10px_rgba(0,0,0,0.7)] flex flex-col h-full"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {(() => {
                const selectId = "assistant-experiment-select-expanded";
                return (
                  <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-cyan-300 drop-shadow-sm">
                  {t('assistant.title')}
                </h3>
                <div className="flex items-center gap-3">
                  {messages.length > 0 && (
                    <button
                      onClick={handleReset}
                      type="button"
                      className="text-gray-400 text-sm hover:text-red-400 transition-colors duration-150"
                    >
                      {t('assistant.new_chat')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="text-gray-300 hover:text-white text-sm border border-gray-600/70 rounded-md px-2 py-1 transition-colors"
                    aria-label={t('assistant.collapse')}
                  >
                    {t('assistant.collapse')}
                  </button>
                </div>
              </div>

              {experiments.length > 0 && (
                <div className="mb-4">
                  <label htmlFor={selectId} className="block text-sm text-gray-400 mb-1">
                    {t('assistant.experiment_label')}
                  </label>
                  <div className="relative">
                    <select
                      id={selectId}
                      value={selectedExperimentId ?? ''}
                      onChange={(e) => setExperimentId(parseInt(e.target.value, 10))}
                      className="block appearance-none w-full bg-gray-700/70 border border-gray-600 text-white py-2 pl-3 pr-8 rounded-lg leading-tight text-base focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all duration-150 ease-in-out"
                    >
                      <option value="" disabled>
                        {t('assistant.experiment_placeholder')}
                      </option>
                      {experiments.map((experiment) => (
                        <option key={experiment.id} value={experiment.id}>
                          {experiment.name}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                      <svg
                        className="fill-current h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-gray-700 bg-gray-800/40 p-4 space-y-3 scrollbar-thin scrollbar-thumb-gray-700 hover:scrollbar-thumb-gray-600">
                {messages.length === 0 && !thinking ? (
                  renderEmptyState("expanded")
                ) : (
                  <>
                    {messages.map(({ isAi, message }, i) => (
                      <div
                        key={i}
                        className={`p-3 rounded-xl max-w-[85%] ${!isAi
                          ? "ml-auto bg-cyan-500/20 text-cyan-200"
                          : "bg-gray-700/60 text-gray-100"
                          }`}

                      >

                        <p dangerouslySetInnerHTML={{ __html: message }} className="whitespace-pre-wrap" />
                      </div>
                    ))}

                    {thinking && (
                      <div className="p-3 rounded-xl max-w-[85%] bg-gray-700/60 text-gray-100 flex items-center gap-2">
                        <span>{t('assistant.thinking')}</span>
                        <motion.span
                          className="flex gap-1 ml-1"
                          initial={{ opacity: 0.3 }}
                          animate={{
                            opacity: [0.3, 1, 0.3],
                          }}
                          transition={{
                            duration: 1.2,
                            repeat: Infinity,
                            ease: "easeInOut",
                            times: [0, 0.5, 1],
                          }}
                        >
                          <motion.span
                            className="w-2 h-2 bg-cyan-400 rounded-full"
                            animate={{ y: [0, -3, 0] }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              delay: 0,
                            }}
                          />
                          <motion.span
                            className="w-2 h-2 bg-cyan-400 rounded-full"
                            animate={{ y: [0, -3, 0] }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              delay: 0.2,
                            }}
                          />
                          <motion.span
                            className="w-2 h-2 bg-cyan-400 rounded-full"
                            animate={{ y: [0, -3, 0] }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              delay: 0.4,
                            }}
                          />
                        </motion.span>
                      </div>
                    )}
                  </>
                )}
                <div ref={expandedChatEndRef} />
              </div>

              <form
                onSubmit={handleSubmit}
                className="mt-4 flex items-center gap-2 border-t border-gray-700 pt-4"
              >
                <input
                  type="text"
                  value={input}
                  ref={expandedInputRef}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t('assistant.input_placeholder')}
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-200 focus:outline-none focus:border-cyan-400 transition duration-150"
                  disabled={thinking}
                />
                <button
                  type="submit"
                  className={`bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2 rounded-lg font-medium transition-all duration-150 ${thinking ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  disabled={thinking}
                >
                  {t('assistant.send')}
                </button>
              </form>
                  </>
                );
              })()}
            </motion.div>
          </div>
        </div>
      )}

      <motion.div
        className={`bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-2xl border border-gray-700 shadow-[0_0_30px_-10px_rgba(0,0,0,0.6)] flex flex-col h-[60vh] min-h-[440px] max-h-[640px] ${className ?? ""}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
      {(() => {
        const selectId = "assistant-experiment-select";
        return (
          <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-cyan-300 drop-shadow-sm">
          {t('assistant.title')}
        </h3>
          <div className="flex items-center gap-3">
            {messages.length > 0 && (
              <button
                onClick={handleReset}
                type="button"
                className="text-gray-400 text-sm hover:text-red-400 transition-colors duration-150"
              >
                {t('assistant.new_chat')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="text-gray-300 hover:text-white text-sm border border-gray-600/70 rounded-md px-2 py-1 transition-colors"
              aria-label={t('assistant.expand')}
            >
              {t('assistant.expand')}
            </button>
          </div>
      </div>

      {experiments.length > 0 && (
        <div className="mb-4">
          <label htmlFor={selectId} className="block text-sm text-gray-400 mb-1">
            {t('assistant.experiment_label')}
          </label>
          <div className="relative">
            <select
              id={selectId}
              value={selectedExperimentId ?? ''}
              onChange={(e) => setExperimentId(parseInt(e.target.value, 10))}
              className="block appearance-none w-full bg-gray-700/70 border border-gray-600 text-white py-2 pl-3 pr-8 rounded-lg leading-tight text-base focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all duration-150 ease-in-out"
            >
              <option value="" disabled>
                {t('assistant.experiment_placeholder')}
              </option>
              {experiments.map((experiment) => (
                <option key={experiment.id} value={experiment.id}>
                  {experiment.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
              <svg
                className="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-gray-700 bg-gray-800/40 p-4 space-y-3 scrollbar-thin scrollbar-thumb-gray-700 hover:scrollbar-thumb-gray-600">
        {messages.length === 0 && !thinking ? (
          renderEmptyState("inline")
        ) : (
          <>
            {messages.map(({ isAi, message }, i) => (
              <div
                key={i}
                className={`p-3 rounded-xl max-w-[85%] ${!isAi
                  ? "ml-auto bg-cyan-500/20 text-cyan-200"
                  : "bg-gray-700/60 text-gray-100"
                  }`}

              >

                <p dangerouslySetInnerHTML={{ __html: message }} className="whitespace-pre-wrap" />
              </div>
            ))}

            {thinking && (
              <div className="p-3 rounded-xl max-w-[85%] bg-gray-700/60 text-gray-100 flex items-center gap-2">
                <span>{t('assistant.thinking')}</span>
                <motion.span
                  className="flex gap-1 ml-1"
                  initial={{ opacity: 0.3 }}
                  animate={{
                    opacity: [0.3, 1, 0.3],
                  }}
                  transition={{
                    duration: 1.2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    times: [0, 0.5, 1],
                  }}
                >
                  <motion.span
                    className="w-2 h-2 bg-cyan-400 rounded-full"
                    animate={{ y: [0, -3, 0] }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: 0,
                    }}
                  />
                  <motion.span
                    className="w-2 h-2 bg-cyan-400 rounded-full"
                    animate={{ y: [0, -3, 0] }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: 0.2,
                    }}
                  />
                  <motion.span
                    className="w-2 h-2 bg-cyan-400 rounded-full"
                    animate={{ y: [0, -3, 0] }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: 0.4,
                    }}
                  />
                </motion.span>
              </div>
            )}
          </>
        )}
        <div ref={inlineChatEndRef} />
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-4 flex items-center gap-2 border-t border-gray-700 pt-4"
      >
        <input
          type="text"
          value={input}
          ref={inlineInputRef}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('assistant.input_placeholder')}
          className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-200 focus:outline-none focus:border-cyan-400 transition duration-150"
          disabled={thinking}
        />
        <button
          type="submit"
          className={`bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2 rounded-lg font-medium transition-all duration-150 ${thinking ? "opacity-50 cursor-not-allowed" : ""
            }`}
          disabled={thinking}
        >
          {t('assistant.send')}
        </button>
      </form>
          </>
        );
      })()}
    </motion.div>
    </>
  );
};
